import React from 'react';
import { Table, Tag, Progress, Button } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';

interface NetworksTableProps {
  networks: Record<string, any>;
  loading?: boolean;
}

const NetworksTable: React.FC<NetworksTableProps> = ({
  networks,
  loading = false
}) => {
  const { t: networksT } = useTranslationWithNamespace('networks');
  const navigate = useNavigate();

  // Transform networks data for table
  const networkData = Object.entries(networks).map(([network, rpcs]: [string, any], index) => {
    const activeCount = rpcs.filter((rpc: any) => rpc.status === 'active').length;
    const healthPercentage = rpcs.length > 0 ? (activeCount / rpcs.length) * 100 : 0;

    return {
      key: index,
      network,
      totalRPCs: rpcs.length,
      activeRPCs: activeCount,
      healthPercentage: Math.round(healthPercentage),
      status: healthPercentage >= 80 ? 'healthy' :
              healthPercentage >= 50 ? 'warning' : 'unhealthy'
    };
  });

  // Table columns
  const columns = [
    {
      title: networksT('network.title', 'Network'),
      dataIndex: 'network',
      key: 'network',
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: networksT('status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        if (status === 'healthy') {
          return <Tag icon={<CheckCircleOutlined />} color="success">{networksT('healthy', 'Healthy')}</Tag>;
        } else if (status === 'warning') {
          return <Tag icon={<WarningOutlined />} color="warning">{networksT('warning', 'Warning')}</Tag>;
        } else {
          return <Tag icon={<CloseCircleOutlined />} color="error">{networksT('unhealthy', 'Unhealthy')}</Tag>;
        }
      }
    },
    {
      title: networksT('activeRPCs', 'Active RPCs'),
      key: 'rpcs',
      render: (_: unknown, record: any) => `${record.activeRPCs}/${record.totalRPCs}`
    },
    {
      title: networksT('health', 'Health'),
      dataIndex: 'healthPercentage',
      key: 'health',
      render: (health: number) => (
        <Progress
          percent={health}
          size="small"
          status={
            health >= 80 ? 'success' :
            health >= 50 ? 'normal' : 'exception'
          }
        />
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={networkData}
      pagination={false}
      size="small"
      loading={loading}
      title={() => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{networksT('title', 'Networks')}</span>
          <Button type="link" onClick={() => navigate('/rpc')}>
            {networksT('viewAll', 'View All')}
          </Button>
        </div>
      )}
    />
  );
};

export default NetworksTable;
